import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';
import {
  setDoraFunctions,
  addDoraFunction,
  updateDoraFunction as updateDoraFunctionState,
  removeDoraFunction,
  setLoading,
  setError,
  selectDoraFunctions,
  selectFunctionError,
} from '../store/function/functionSlice.ts';
import {
  listDoraFunctions,
  createDoraFunction,
  updateDoraFunction,
  deleteDoraFunction,
  getLicensedActivities,
  getCriticalityOptions,
  getImpactOptions,
} from '../../../api.ts';
import { DoraFunction } from '../models/doraFunction.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

// Editable row interface for Excel-like functions table
interface EditableFunctionRow {
  id?: number;
  function_identifier: string; // Display only, auto-generated
  function_name: string;
  financial_entity_lei: string;
  licensed_activity: string;
  criticality_assessment: string;
  criticality_reasons: string;
  last_assessment_date: string;
  recovery_time_objective_hours: number | '';
  recovery_point_objective_hours: number | '';
  discontinuation_impact: string;
  description: string;
  isNew?: boolean;
  touched?: boolean;
}

const FunctionsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const functions = useAppSelector(selectDoraFunctions);
  const error = useAppSelector(selectFunctionError);
  const currentOrg = useAppSelector(selectCurrentOrganization);

  const [editableFunctions, setEditableFunctions] = useState<EditableFunctionRow[]>([]);
  const [licensedActivities, setLicensedActivities] = useState<string[]>([]);
  const [criticalityOptions, setCriticalityOptions] = useState<string[]>([]);
  const [impactOptions, setImpactOptions] = useState<string[]>([]);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableFunctionRow>();

  const canEdit = Boolean(currentOrg?.role && currentOrg.role !== 'viewer');

  // Field order for Tab navigation
  const fieldOrder: (keyof EditableFunctionRow)[] = [
    'function_name',
    'financial_entity_lei',
    'licensed_activity',
    'criticality_assessment',
    'discontinuation_impact',
    'last_assessment_date',
    'recovery_time_objective_hours',
    'recovery_point_objective_hours',
    'criticality_reasons',
  ];

  useEffect(() => {
    loadFunctions();
    loadOptions();
  }, []);

  const loadFunctions = async () => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const data = await listDoraFunctions();
      dispatch(setDoraFunctions(data));
    } catch (err: any) {
      dispatch(setError(err?.response?.data?.detail || 'Failed to load functions'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const loadOptions = async () => {
    try {
      const [activities, criticality, impact] = await Promise.all([
        getLicensedActivities(),
        getCriticalityOptions(),
        getImpactOptions(),
      ]);
      setLicensedActivities(activities);
      setCriticalityOptions(criticality);
      setImpactOptions(impact);
    } catch (err) {
      console.error('Failed to load options:', err);
    }
  };

  // Helper to determine if a required field is empty
  const isRequiredFieldEmpty = (row: EditableFunctionRow, field: keyof EditableFunctionRow): boolean => {
    const requiredFields: (keyof EditableFunctionRow)[] = [
      'function_name',
      'financial_entity_lei',
      'licensed_activity',
      'criticality_assessment',
      'discontinuation_impact',
      'last_assessment_date',
    ];
    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  // Helper to get cell background color with amber shine for required empty fields
  const getCellBackgroundColor = (row: EditableFunctionRow, field: keyof EditableFunctionRow): string => {
    // Don't highlight untouched new rows
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  const updateFunctionRow = (index: number, field: keyof EditableFunctionRow, value: any) => {
    setEditableFunctions(prev => {
      const updated = [...prev];
      const row = updated[index];
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {})
      };
      return updated;
    });
  };

  const handleFunctionRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableFunctions, saveFunctionRow);
  };

  const handleFunctionRowBlur = () => {
    baseHandleRowBlur(editableFunctions, saveFunctionRow);
  };

  const handleFunctionTabNavigation = (
    e: React.KeyboardEvent,
    currentRow: number,
    currentField: keyof EditableFunctionRow
  ) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableFunctions.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleFunctionRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const saveFunctionRow = useCallback(async (row: EditableFunctionRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    // Prevent duplicate saves
    if (isSaving(rowKey)) return;

    try {
      if (row.isNew) {
        // Create new function only if required fields are filled
        if (
          row.function_name.trim() &&
          row.financial_entity_lei.length === 20 &&
          row.licensed_activity &&
          row.criticality_assessment &&
          row.last_assessment_date &&
          row.discontinuation_impact
        ) {
          markSaving(rowKey);
          const created = await createDoraFunction({
            function_name: row.function_name.trim(),
            financial_entity_lei: row.financial_entity_lei.trim(),
            licensed_activity: row.licensed_activity,
            criticality_assessment: row.criticality_assessment,
            criticality_reasons: row.criticality_reasons || null,
            last_assessment_date: row.last_assessment_date,
            recovery_time_objective_hours: Number(row.recovery_time_objective_hours) || 0,
            recovery_point_objective_hours: Number(row.recovery_point_objective_hours) || 0,
            discontinuation_impact: row.discontinuation_impact,
            description: row.description || null,
          });
          dispatch(addDoraFunction(created));
        }
      } else if (row.id) {
        // Update existing function
        if (
          row.function_name.trim() &&
          row.financial_entity_lei.length === 20 &&
          row.licensed_activity &&
          row.criticality_assessment &&
          row.last_assessment_date &&
          row.discontinuation_impact
        ) {
          markSaving(rowKey);
          const updated = await updateDoraFunction(row.id, {
            function_name: row.function_name.trim(),
            financial_entity_lei: row.financial_entity_lei.trim(),
            licensed_activity: row.licensed_activity,
            criticality_assessment: row.criticality_assessment,
            criticality_reasons: row.criticality_reasons || null,
            last_assessment_date: row.last_assessment_date,
            recovery_time_objective_hours: Number(row.recovery_time_objective_hours) || 0,
            recovery_point_objective_hours: Number(row.recovery_point_objective_hours) || 0,
            discontinuation_impact: row.discontinuation_impact,
            description: row.description || null,
          });
          dispatch(updateDoraFunctionState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save function', err);
      dispatch(setError(err?.response?.data?.detail || 'Failed to save function'));
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  const handleFunctionPaste = useCallback((
    e: React.ClipboardEvent,
    startRowIndex: number,
    startField: keyof EditableFunctionRow
  ) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    const parsedRows = parsePasteData(pasteData);

    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    const createEmptyRow = (): EditableFunctionRow => ({
      function_identifier: '', // Auto-generated
      function_name: '',
      financial_entity_lei: '',
      licensed_activity: licensedActivities[0] || '',
      criticality_assessment: 'Not critical or important',
      criticality_reasons: '',
      last_assessment_date: '9999-12-31',
      recovery_time_objective_hours: 0,
      recovery_point_objective_hours: 0,
      discontinuation_impact: 'Low',
      description: '',
      isNew: true,
      touched: true,
    });

    setEditableFunctions(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        new Map(),
        createEmptyRow
      );
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    setTimeout(() => {
      normalizedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableFunctions.length) {
          saveFunctionRow(editableFunctions[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableFunctions, saveFunctionRow, fieldOrder, licensedActivities]);

  const handleDelete = async (func: EditableFunctionRow) => {
    if (!func.id) return;
    if (!window.confirm(`Delete function ${func.function_identifier} (${func.function_name})?`)) return;

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      await deleteDoraFunction(func.id);
      dispatch(removeDoraFunction(func.id));
    } catch (err: any) {
      dispatch(setError(err?.response?.data?.detail || 'Failed to delete function'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Convert functions to editable rows with empty row at end
  const rows = useMemo(() => {
    const existingRows: EditableFunctionRow[] = functions.map(f => ({
      id: f.id,
      function_identifier: f.function_identifier,
      function_name: f.function_name,
      financial_entity_lei: f.financial_entity_lei,
      licensed_activity: f.licensed_activity,
      criticality_assessment: f.criticality_assessment,
      criticality_reasons: f.criticality_reasons || '',
      last_assessment_date: f.last_assessment_date,
      recovery_time_objective_hours: f.recovery_time_objective_hours,
      recovery_point_objective_hours: f.recovery_point_objective_hours,
      discontinuation_impact: f.discontinuation_impact,
      description: f.description || '',
    }));

    const emptyRow: EditableFunctionRow = {
      function_identifier: '', // Auto-generated
      function_name: '',
      financial_entity_lei: '',
      licensed_activity: '',
      criticality_assessment: 'Not critical or important',
      criticality_reasons: '',
      last_assessment_date: '9999-12-31',
      recovery_time_objective_hours: '',
      recovery_point_objective_hours: '',
      discontinuation_impact: 'Low',
      description: '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [functions]);

  useEffect(() => {
    setEditableFunctions(rows);
  }, [rows]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Functions (B_06.01)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Function Identification</Typography>
        <Typography variant="caption" color="text.secondary">
          Click cells to edit • Tab to navigate • Changes save automatically
        </Typography>
      </Stack>

      <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Function ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Function Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>LEI</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Licensed Activity</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Criticality</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Impact</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Assessment Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>RTO (hrs)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>RPO (hrs)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>Reasons</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editableFunctions.map((row, index) => {
              const isFocused = focusedRowIndex === index;
              const isEvenRow = index % 2 === 0;

              return (
                <TableRow
                  key={row.id ?? `new-${index}`}
                  sx={{
                    bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                    '&:hover': {
                      bgcolor: getRowHoverColor(isFocused),
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {/* Function Identifier (read-only) */}
                  <TableCell width="8%">
                    <div style={{ padding: '8px', minHeight: '20px' }}>
                      {row.function_identifier || (row.isNew ? <span style={{ color: '#999' }}>Auto</span> : '')}
                    </div>
                  </TableCell>

                  {/* Function Name */}
                  <TableCell width="15%">
                    {editingCell?.row === index && editingCell?.field === 'function_name' ? (
                      <TextField
                        value={row.function_name}
                        onChange={(e) => updateFunctionRow(index, 'function_name', e.target.value)}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'function_name')}
                        onPaste={(e) => handleFunctionPaste(e, index, 'function_name')}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder={row.isNew ? 'Enter name...' : ''}
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'function_name' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'function_name'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.function_name || (row.isNew ? <span style={{ color: '#999' }}>Enter name...</span> : '')}
                      </div>
                    )}
                  </TableCell>

                  {/* Financial Entity LEI */}
                  <TableCell width="12%">
                    {editingCell?.row === index && editingCell?.field === 'financial_entity_lei' ? (
                      <TextField
                        value={row.financial_entity_lei}
                        onChange={(e) => updateFunctionRow(index, 'financial_entity_lei', e.target.value.toUpperCase())}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'financial_entity_lei')}
                        onPaste={(e) => handleFunctionPaste(e, index, 'financial_entity_lei')}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder={row.isNew ? '20-char LEI...' : ''}
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        inputProps={{ maxLength: 20 }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'financial_entity_lei' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'financial_entity_lei'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.financial_entity_lei || (row.isNew ? <span style={{ color: '#999' }}>20-char LEI...</span> : '')}
                      </div>
                    )}
                  </TableCell>

                  {/* Licensed Activity */}
                  <TableCell width="15%">
                    {editingCell?.row === index && editingCell?.field === 'licensed_activity' ? (
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={row.licensed_activity || ''}
                          onChange={(e: SelectChangeEvent<string>) => {
                            updateFunctionRow(index, 'licensed_activity', e.target.value);
                          }}
                          onBlur={() => {
                            handleFunctionRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'licensed_activity')}
                          displayEmpty
                          disableUnderline
                          sx={{ '& .MuiSelect-select': { p: 1 } }}
                          disabled={!canEdit}
                        >
                          {row.isNew && <MenuItem value=""><em>Select activity...</em></MenuItem>}
                          {licensedActivities.map(activity => (
                            <MenuItem key={activity} value={activity}>{activity}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'licensed_activity' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'pointer' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'licensed_activity'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.licensed_activity || (row.isNew ? <span style={{ color: '#999' }}>Select activity...</span> : '')}
                      </div>
                    )}
                  </TableCell>

                  {/* Criticality Assessment */}
                  <TableCell width="10%">
                    {editingCell?.row === index && editingCell?.field === 'criticality_assessment' ? (
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={row.criticality_assessment || ''}
                          onChange={(e: SelectChangeEvent<string>) => {
                            updateFunctionRow(index, 'criticality_assessment', e.target.value);
                          }}
                          onBlur={() => {
                            handleFunctionRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'criticality_assessment')}
                          displayEmpty
                          disableUnderline
                          sx={{ '& .MuiSelect-select': { p: 1 } }}
                          disabled={!canEdit}
                        >
                          {criticalityOptions.map(option => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'criticality_assessment' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'pointer' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'criticality_assessment'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.criticality_assessment || ''}
                      </div>
                    )}
                  </TableCell>

                  {/* Discontinuation Impact */}
                  <TableCell width="8%">
                    {editingCell?.row === index && editingCell?.field === 'discontinuation_impact' ? (
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={row.discontinuation_impact || ''}
                          onChange={(e: SelectChangeEvent<string>) => {
                            updateFunctionRow(index, 'discontinuation_impact', e.target.value);
                          }}
                          onBlur={() => {
                            handleFunctionRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'discontinuation_impact')}
                          displayEmpty
                          disableUnderline
                          sx={{ '& .MuiSelect-select': { p: 1 } }}
                          disabled={!canEdit}
                        >
                          {impactOptions.map(option => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'discontinuation_impact' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'pointer' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'discontinuation_impact'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.discontinuation_impact || ''}
                      </div>
                    )}
                  </TableCell>

                  {/* Last Assessment Date */}
                  <TableCell width="10%">
                    {editingCell?.row === index && editingCell?.field === 'last_assessment_date' ? (
                      <TextField
                        type="date"
                        value={row.last_assessment_date}
                        onChange={(e) => updateFunctionRow(index, 'last_assessment_date', e.target.value)}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'last_assessment_date')}
                        size="small"
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'last_assessment_date' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'last_assessment_date'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.last_assessment_date || '9999-12-31'}
                      </div>
                    )}
                  </TableCell>

                  {/* RTO */}
                  <TableCell width="6%">
                    {editingCell?.row === index && editingCell?.field === 'recovery_time_objective_hours' ? (
                      <TextField
                        type="number"
                        value={row.recovery_time_objective_hours}
                        onChange={(e) => updateFunctionRow(index, 'recovery_time_objective_hours', e.target.value === '' ? '' : Number(e.target.value))}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'recovery_time_objective_hours')}
                        onPaste={(e) => handleFunctionPaste(e, index, 'recovery_time_objective_hours')}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        inputProps={{ min: 0 }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'recovery_time_objective_hours' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.recovery_time_objective_hours !== '' ? row.recovery_time_objective_hours : (row.isNew ? <span style={{ color: '#999' }}>0</span> : '0')}
                      </div>
                    )}
                  </TableCell>

                  {/* RPO */}
                  <TableCell width="6%">
                    {editingCell?.row === index && editingCell?.field === 'recovery_point_objective_hours' ? (
                      <TextField
                        type="number"
                        value={row.recovery_point_objective_hours}
                        onChange={(e) => updateFunctionRow(index, 'recovery_point_objective_hours', e.target.value === '' ? '' : Number(e.target.value))}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'recovery_point_objective_hours')}
                        onPaste={(e) => handleFunctionPaste(e, index, 'recovery_point_objective_hours')}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        inputProps={{ min: 0 }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'recovery_point_objective_hours' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.recovery_point_objective_hours !== '' ? row.recovery_point_objective_hours : (row.isNew ? <span style={{ color: '#999' }}>0</span> : '0')}
                      </div>
                    )}
                  </TableCell>

                  {/* Criticality Reasons */}
                  <TableCell width="15%">
                    {editingCell?.row === index && editingCell?.field === 'criticality_reasons' ? (
                      <TextField
                        value={row.criticality_reasons}
                        onChange={(e) => updateFunctionRow(index, 'criticality_reasons', e.target.value)}
                        onBlur={() => {
                          handleFunctionRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => handleFunctionTabNavigation(e, index, 'criticality_reasons')}
                        onPaste={(e) => handleFunctionPaste(e, index, 'criticality_reasons')}
                        size="small"
                        fullWidth
                        variant="standard"
                        placeholder="Optional..."
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& input': { p: 1 } }}
                        inputProps={{ maxLength: 300 }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleFunctionRowFocus(index);
                            setEditingCell({ row: index, field: 'criticality_reasons' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.criticality_reasons || '—'}
                      </div>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    {!row.isNew && (
                      <IconButton
                        onClick={() => handleDelete(row)}
                        size="small"
                        disabled={!canEdit}
                        sx={{ opacity: 0.6, '&:hover': { opacity: 1, color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {functions.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No functions found. Start typing in the empty row to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FunctionsPanel;
